import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Bath, BedDouble, Calendar, ChevronLeft, ChevronRight, Clock, Compass, Info, MapPin, Phone, Send, Sparkles, Star, Users, X } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { PROPERTY } from "@/constants/testIds";
import { toast } from "sonner";
import PropertyCard from "@/components/PropertyCard";

const CHECKIN_TIMES = ["12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM"];

export default function PropertyDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const openLightbox = (i) => setLightbox(i);
  const closeLightbox = () => setLightbox(null);
  const nextImg = (e) => { e?.stopPropagation(); setLightbox((lightbox + 1) % (p?.images?.length || 1)); };
  const prevImg = (e) => { e?.stopPropagation(); setLightbox((lightbox - 1 + (p?.images?.length || 1)) % (p?.images?.length || 1)); };

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowRight") nextImg();
      else if (e.key === "ArrowLeft") prevImg();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [lightbox]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    guest_name: "", email: "", phone: "",
    checkin_date: "", checkout_date: "",
    checkin_time: "3:00 PM", num_guests: 2, notes: "",
  });

  useEffect(() => {
    api.get(`/properties/${slug}`).then(r => {
      setP(r.data);
      setActive(0);
      api.get("/properties", { params: { destination: r.data.destination, limit: 5 } })
        .then(s => setSimilar(s.data.filter(x => x.slug !== r.data.slug).slice(0, 3)));
    });
  }, [slug]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "number" ? +e.target.value : e.target.value });

  const submitBooking = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/bookings", { property_slug: p.slug, ...form });
      toast.success("Enquiry sent! Our concierge will call you within 2 hours.");
      setForm({ guest_name: "", email: "", phone: "", checkin_date: "", checkout_date: "",
                checkin_time: "3:00 PM", num_guests: 2, notes: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send enquiry. Please try again.");
    } finally { setBusy(false); }
  };

  if (!p) return <div className="pt-40 pb-24 container-tarzan">Loading…</div>;

  return (
    <div className="pt-28 pb-24 bg-ivory">
      <div className="container-tarzan">
        <Link to="/stays" className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-forest mb-6"><ArrowLeft className="h-4 w-4" /> Back to all stays</Link>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-7">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="eyebrow text-forest inline-flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {p.destination}, {p.state}</span>
              {!p.is_bookable && (
                <span className="text-[10px] uppercase tracking-[0.22em] px-3 py-1 rounded-full bg-sand-100 text-forest-700 font-semibold">Coming Soon</span>
              )}
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-ink mt-2 leading-[1.02] tracking-tight text-balance">{p.name}</h1>
            <p className="text-ink-soft mt-2 text-base md:text-lg">{p.tagline}</p>
          </div>
          <div className="flex items-center gap-2 bg-forest-50 px-4 py-2 rounded-full self-start">
            <Star className="h-4 w-4 fill-ember stroke-ember" />
            <span className="text-sm font-semibold text-forest num">{p.rating.toFixed(1)}</span>
            <span className="text-xs text-ink-soft">({p.reviews_count} reviews)</span>
          </div>
        </div>

        <div className="grid grid-cols-4 grid-rows-2 gap-3 md:gap-4 h-[420px] md:h-[560px] rounded-3xl overflow-hidden" data-testid={PROPERTY.galleryMain}>
          <button onClick={()=>openLightbox(active)} className="col-span-4 md:col-span-2 row-span-2 overflow-hidden" data-testid="property-gallery-open">
            <img src={p.images[active]} alt={p.name} className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-700" />
          </button>
          {p.images.slice(1, 5).map((img, i) => (
            <button key={i} onClick={()=>openLightbox(i + 1)} className="col-span-2 md:col-span-1 row-span-1 hidden md:block overflow-hidden">
              <img src={img} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </button>
          ))}
        </div>

        {lightbox !== null && (
          <div onClick={closeLightbox} className="fixed inset-0 z-[70] bg-forest-900/95 grid place-items-center p-6 backdrop-blur" data-testid="property-lightbox">
            <button onClick={(e)=>{e.stopPropagation(); closeLightbox();}} data-testid="property-lightbox-close" className="absolute top-5 right-5 h-11 w-11 rounded-full bg-ivory/10 border border-ivory/25 grid place-items-center text-ivory hover:bg-ivory hover:text-forest-900 transition-colors z-10"><X className="h-5 w-5" /></button>
            <button onClick={prevImg} data-testid="property-lightbox-prev" aria-label="Previous" className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 h-12 w-12 md:h-14 md:w-14 rounded-full bg-ivory/10 border border-ivory/25 grid place-items-center text-ivory hover:bg-ivory hover:text-forest-900 transition-colors"><ChevronLeft className="h-6 w-6" /></button>
            <button onClick={nextImg} data-testid="property-lightbox-next" aria-label="Next" className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 h-12 w-12 md:h-14 md:w-14 rounded-full bg-ivory/10 border border-ivory/25 grid place-items-center text-ivory hover:bg-ivory hover:text-forest-900 transition-colors"><ChevronRight className="h-6 w-6" /></button>
            <img src={p.images[lightbox]} alt="" onClick={(e)=>e.stopPropagation()} className="max-w-[92vw] max-h-[85vh] rounded-2xl luxe-shadow object-contain" />
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-ivory/70 text-xs tracking-[0.22em] uppercase">{lightbox + 1} / {p.images.length}</div>
          </div>
        )}

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            <div className="flex flex-wrap gap-x-10 gap-y-4 pb-8 border-b border-forest/10">
              <Fact icon={BedDouble} v={`${p.bedrooms} Bedrooms`} />
              <Fact icon={Bath}      v={`${p.bathrooms} Bathrooms`} />
              <Fact icon={Users}     v={`Up to ${p.max_guests} guests`} />
              <Fact icon={Compass}   v={p.experience.replaceAll("_"," ")} />
            </div>

            <div>
              <h2 className="font-serif text-3xl text-ink mb-4">A quiet rebellion against the ordinary.</h2>
              <p className="text-ink-soft leading-relaxed text-base md:text-lg whitespace-pre-line">{p.description}</p>
            </div>

            <div>
              <h2 className="font-serif text-3xl text-ink mb-6">What this home offers</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {p.amenities.map(a => (
                  <div key={a} className="flex items-center gap-3 bg-white border border-forest/10 rounded-2xl p-4 soft-shadow">
                    <Sparkles className="h-4 w-4 text-forest-400" />
                    <span className="text-sm text-ink">{a}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-forest-50/60 rounded-3xl p-8 md:p-10">
              <div className="eyebrow mb-3">Your host</div>
              <h3 className="font-serif text-2xl text-ink">{p.host_name}</h3>
              <p className="text-ink-soft mt-2 text-sm md:text-base leading-relaxed">{p.host_tagline}. Every booking is supported by Tarzan's on-ground concierge team — reachable at <a href="tel:+919491317777" className="text-forest font-medium underline-offset-4 hover:underline">+91 94913 17777</a> for anything you need before or during your stay.</p>
            </div>
          </div>

          <aside className="lg:sticky lg:top-28 self-start">
            {p.is_bookable ? (
              <form onSubmit={submitBooking} className="bg-white border border-forest/10 rounded-3xl p-6 md:p-7 luxe-shadow" data-testid="booking-form">
                <div className="flex items-baseline gap-2">
                  <span className="font-serif text-3xl text-forest num">{formatINR(p.price_per_night)}</span>
                  <span className="text-sm text-ink-soft">/ night</span>
                </div>
                <p className="text-xs text-ink-soft mt-1">Send an enquiry — our concierge confirms availability and pricing within 2 hours.</p>

                <div className="mt-6 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Check In" required>
                      <input required type="date" value={form.checkin_date} onChange={set("checkin_date")} data-testid="book-checkin" className={inp} />
                    </Field>
                    <Field label="Check Out" required>
                      <input required type="date" value={form.checkout_date} onChange={set("checkout_date")} data-testid="book-checkout" className={inp} />
                    </Field>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Guests" required>
                      <input required type="number" min={1} max={p.max_guests} value={form.num_guests} onChange={set("num_guests")} data-testid="book-guests" className={inp} />
                    </Field>
                    <Field label="Check-in time">
                      <select value={form.checkin_time} onChange={set("checkin_time")} data-testid="book-checkin-time" className={inp}>
                        {CHECKIN_TIMES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </Field>
                  </div>
                  <Field label="Full name" required>
                    <input required value={form.guest_name} onChange={set("guest_name")} data-testid="book-name" className={inp} />
                  </Field>
                  <Field label="Email" required>
                    <input required type="email" value={form.email} onChange={set("email")} data-testid="book-email" className={inp} />
                  </Field>
                  <Field label="Phone" required>
                    <input required value={form.phone} onChange={set("phone")} placeholder="+91 …" data-testid="book-phone" className={inp} />
                  </Field>
                  <Field label="Notes (optional)">
                    <textarea rows={3} value={form.notes} onChange={set("notes")} placeholder="Anything we should know?" data-testid="book-notes" className={`${inp} resize-none`} />
                  </Field>
                </div>

                <button
                  disabled={busy}
                  data-testid={PROPERTY.bookBtn}
                  className="mt-6 w-full bg-forest text-ivory py-4 rounded-full font-medium text-sm hover:bg-forest-700 transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {busy ? "Sending…" : "Send Booking Enquiry"} <Send className="h-4 w-4" />
                </button>
                <p className="text-[11px] text-ink-soft mt-3 text-center">You won't be charged yet. Our team confirms availability first.</p>
              </form>
            ) : (
              <div className="bg-white border border-forest/10 rounded-3xl p-8 luxe-shadow text-center" data-testid="coming-soon-card">
                <div className="inline-flex items-center gap-2 bg-sand-100 text-forest-700 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.22em] font-semibold mb-5">
                  <Info className="h-3.5 w-3.5" /> Coming Soon
                </div>
                <h3 className="font-serif text-2xl text-ink">Bookings open shortly</h3>
                <p className="text-ink-soft text-sm mt-3 leading-relaxed">
                  This retreat is joining the Tarzan collection soon. For an early booking or a personal walk-through, talk to our concierge.
                </p>
                <a
                  href="tel:+919491317777"
                  data-testid="coming-soon-call"
                  className="mt-6 inline-flex items-center justify-center gap-2 w-full bg-forest text-ivory py-3.5 rounded-full font-medium text-sm hover:bg-forest-700 transition-all"
                >
                  <Phone className="h-4 w-4" /> Call +91 94913 17777
                </a>
                <Link
                  to="/contact"
                  data-testid={PROPERTY.inquireBtn}
                  className="mt-3 inline-flex items-center justify-center w-full border border-forest/30 text-forest py-3 rounded-full font-medium text-sm hover:bg-forest hover:text-ivory transition-colors"
                >
                  Write to concierge
                </Link>
                <p className="text-[11px] text-ink-soft mt-4">Or explore our currently bookable stay in <Link to="/stays/tarzan-hyderabad-retreat" className="text-forest font-medium underline-offset-4 hover:underline">Hyderabad</Link>.</p>
              </div>
            )}
          </aside>
        </div>

        {similar.length > 0 && (
          <div className="mt-24">
            <h2 className="font-serif text-3xl md:text-4xl text-ink mb-8">More stays in {p.destination}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
              {similar.map(s => <PropertyCard key={s.id} p={s} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const inp = "w-full border border-forest/15 rounded-xl bg-transparent px-3 py-2.5 outline-none focus:border-forest text-sm text-ink";

function Fact({ icon: Icon, v }) {
  return (
    <div className="flex items-center gap-2 text-sm text-ink">
      <Icon className="h-4 w-4 text-forest-400" strokeWidth={1.5} /> <span className="capitalize">{v}</span>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft font-semibold mb-1.5">{label}{required && " *"}</div>
      {children}
    </label>
  );
}
